export interface HeightParseResult {
  valid: boolean;
  cm?: number;
  formatted?: string;
  error?: string;
}

export interface AgeParseResult {
  valid: boolean;
  error?: string;
}

export function parseAge(input: string | number): AgeParseResult {
  const age = typeof input === 'string' ? parseInt(input, 10) : input;
  
  if (isNaN(age) || !input) {
    return { valid: true }; // Empty is allowed (optional field)
  }
  
  if (age < 18) {
    return { valid: false, error: "Must be 18 or older" };
  }
  
  if (age > 120) {
    return { valid: false, error: "Please enter a valid age" };
  }
  
  return { valid: true };
}

export function parseHeight(input: string): HeightParseResult {
  // Normalize smart/curly quotes from mobile keyboards to straight quotes
  const normalized = input.trim()
    .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")  // smart single quotes → '
    .replace(/[\u201C\u201D]/g, '"');               // smart double quotes → "
  const trimmed = normalized.toLowerCase();
  
  if (!trimmed) {
    return { valid: true }; // Empty is allowed (optional field)
  }

  // Try feet and inches: 5'10", 5'10, 5ft10in, 5ft 10in, 5 10, etc.
  const feetInchesRegex = /^(\d+)\s*['′ft]?\s*(\d+)?\s*["″in]?$/;
  const feetOnlyRegex = /^(\d+)\s*['′]?\s*(?:ft|feet)$/;
  
  // Try cm: 178cm, 178 cm, 178
  const cmRegex = /^(\d{2,3})\s*(?:cm)?$/;
  
  // Try meters: 1.78m, 1.78 m
  const metersRegex = /^(\d+(?:\.\d+)?)\s*m$/;

  let heightCm: number | null = null;

  // Check feet and inches first
  const feetInchesMatch = trimmed.match(feetInchesRegex);
  if (feetInchesMatch) {
    const feet = parseInt(feetInchesMatch[1], 10);
    const inches = feetInchesMatch[2] ? parseInt(feetInchesMatch[2], 10) : 0;
    
    if (inches > 11) {
      return { valid: false, error: "Inches should be 0-11" };
    }
    
    heightCm = (feet * 30.48) + (inches * 2.54);
  }

  // Check feet only
  if (heightCm === null) {
    const feetOnlyMatch = trimmed.match(feetOnlyRegex);
    if (feetOnlyMatch) {
      const feet = parseInt(feetOnlyMatch[1], 10);
      heightCm = feet * 30.48;
    }
  }

  // Check centimeters
  if (heightCm === null) {
    const cmMatch = trimmed.match(cmRegex);
    if (cmMatch) {
      heightCm = parseInt(cmMatch[1], 10);
    }
  }

  // Check meters
  if (heightCm === null) {
    const metersMatch = trimmed.match(metersRegex);
    if (metersMatch) {
      heightCm = parseFloat(metersMatch[1]) * 100;
    }
  }

  // If nothing matched, invalid format
  if (heightCm === null) {
    return { 
      valid: false, 
      error: "Try formats like 5'10\", 178cm, or 1.78m" 
    };
  }

  // Validate reasonable range (90cm to 250cm, roughly 3ft to 8ft2in)
  if (heightCm < 90) {
    return { valid: false, error: "Height seems too low. Please double-check." };
  }
  if (heightCm > 250) {
    return { valid: false, error: "Height seems too high. Please double-check." };
  }

  // Format nicely for display
  const totalInches = heightCm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  const formatted = `${feet}'${inches}"`;

  return { valid: true, cm: Math.round(heightCm), formatted };
}
